import { ArrowRight, Zap, Users, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { usePlatformStats, useAnimatedCounter } from "@/hooks/usePlatformStats";

const AnimatedStat = ({ value, glowPulse }: { value: number; glowPulse: boolean }) => {
  const display = useAnimatedCounter(value, 800);
  return (
    <span className={`stat-number font-heading text-3xl md:text-4xl font-bold text-foreground transition-all duration-500 ${glowPulse ? 'text-primary drop-shadow-[0_0_12px_hsl(var(--primary)/0.6)]' : ''}`}>
      {display}+
    </span>
  );
};

const HeroSection = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { stats, glowPulse } = usePlatformStats();

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-1/4 end-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      
      {/* Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="synra-container relative z-10">
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-in">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">{t("hero.badge")}</span>
          </div>

          {/* Main Heading */}
          <h1 className="mx-auto mb-6 max-w-4xl text-center font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {t("hero.title")}{' '}
            <span className="synra-gradient-text">{t("hero.titleHighlight")}</span>
          </h1>

          {/* Subheading */}
          <p className="mx-auto mb-10 max-w-2xl text-center text-lg md:text-xl text-muted-foreground animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.2s' }}>
            {t("hero.subtitle")}
          </p>

          {/* CTA Buttons */}
          <div className="mb-16 flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Button variant="synra" size="xl" className="group transition-all duration-[250ms] ease-in-out hover:scale-105 hover:shadow-glow-strong" onClick={() => navigate("/auth?tab=partner")}>
              {t("hero.cta.partner")}
              <ArrowRight className={`w-5 h-5 transition-transform duration-200 group-hover:translate-x-1 ${isRTL ? 'rtl-flip' : ''}`} />
            </Button>
            <Button variant="synra-outline" size="xl" className="transition-all duration-[250ms] ease-in-out hover:scale-105 hover:shadow-glow" onClick={() => navigate("/auth?tab=youth")}>
              {t("hero.cta.youth")}
            </Button>
          </div>

          {/* Stats */}
          <div className="mx-auto grid w-full max-w-2xl grid-cols-1 gap-8 sm:grid-cols-3 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className={`w-5 h-5 text-primary transition-all duration-500 ${glowPulse ? 'drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]' : ''}`} />
                <AnimatedStat value={stats.approved_partners} glowPulse={glowPulse} />
              </div>
              <p className="text-sm text-muted-foreground">{t("hero.stat.partners")}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Zap className={`w-5 h-5 text-primary transition-all duration-500 ${glowPulse ? 'drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]' : ''}`} />
                <AnimatedStat value={stats.completed_projects} glowPulse={glowPulse} />
              </div>
              <p className="text-sm text-muted-foreground">{t("hero.stat.automations")}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <GraduationCap className={`w-5 h-5 text-primary transition-all duration-500 ${glowPulse ? 'drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]' : ''}`} />
                <AnimatedStat value={stats.approved_youth} glowPulse={glowPulse} />
              </div>
              <p className="text-sm text-muted-foreground">{t("hero.stat.youth")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroSection;